import { useState, useEffect, useRef } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getPendingDraftStatic } from '@/hooks/useDraftProtection';
import { TOAST } from '@/lib/toastMessages';

const DEBUG = import.meta.env.DEV;

const log = (emoji: string, message: string, data?: any) => {
  if (!DEBUG) return;
  const style = 'color: #f59e0b; font-weight: bold;';
  if (data) {
    console.log(`%c[OfflineBanner] ${emoji} ${message}`, style, data);
  } else {
    console.log(`%c[OfflineBanner] ${emoji} ${message}`, style);
  }
};

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);
  const [hasPendingDraft, setHasPendingDraft] = useState(false);
  const wasOfflineRef = useRef(false);

  // Check for pending draft periodically while offline
  useEffect(() => {
    const checkPendingDraft = () => {
      setHasPendingDraft(getPendingDraftStatic() !== null);
    };

    checkPendingDraft();
    const interval = setInterval(checkPendingDraft, 2000);
    return () => clearInterval(interval);
  }, [isOffline]);

  useEffect(() => {
    const handleOnline = () => {
      log('🌐', 'Online event received');
      setIsOffline(false);
      // DraftSyncManager handles the actual sync and toast
      wasOfflineRef.current = false;
    };

    const handleOffline = () => {
      log('📴', 'Offline event received');
      setIsOffline(true);
      wasOfflineRef.current = true;
    };

    // Dev mode: handle simulated offline toggle
    const handleDevOfflineToggle = (event: CustomEvent<{ offline: boolean }>) => {
      log('🔧', 'Dev offline toggle received', event.detail);
      if (event.detail.offline) {
        setIsOffline(true);
        wasOfflineRef.current = true;
      } else {
        setIsOffline(false);
        wasOfflineRef.current = false;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('dev-offline-toggle', handleDevOfflineToggle as EventListener);
    log('👂', 'Listening for connectivity events', { currentlyOffline: !navigator.onLine });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('dev-offline-toggle', handleDevOfflineToggle as EventListener);
    };
  }, []);

  const checkConnection = async () => {
    log('🔍', 'Manual connection check initiated');
    setIsChecking(true);
    try {
      const response = await fetch(window.location.origin, { 
        method: 'HEAD',
        cache: 'no-store' 
      });
        if (response.ok && navigator.onLine) {
        log('✅', 'Connection check successful');
        setIsOffline(false);
        wasOfflineRef.current = false;
        
        // Notify DevTestingPanel to sync its offline toggle state
        window.dispatchEvent(new CustomEvent('dev-online-restored'));
        
        // Try to sync any pending drafts
        const syncManager = (window as any).__draftSyncManager;
        if (syncManager?.hasPendingDraft()) {
          log('📬', 'Found pending draft, triggering sync');
          const synced = await syncManager.attemptSync(true);
          if (!synced) {
            log('❌', 'Sync failed after connection restore');
            toast({
              ...TOAST.NETWORK.BACK_ONLINE,
              description: "However, draft sync failed. Please save manually.",
              variant: "destructive",
            });
          }
        } else {
          log('📭', 'No pending draft to sync');
          toast(TOAST.NETWORK.BACK_ONLINE);
        }
      }
    } catch (error) {
      log('❌', 'Connection check failed', error);
      toast({
        title: "Still offline",
        description: "Please check your internet connection.",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 animate-in slide-in-from-top duration-300">
      <WifiOff className="w-4 h-4" />
      <span>
        {hasPendingDraft 
          ? "You're offline. Your current draft is saved locally and will sync when reconnected."
          : "You're offline. Any changes will be saved locally until you reconnect."
        }
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={checkConnection}
        disabled={isChecking}
        className="ml-2 h-6 px-2 text-destructive-foreground hover:bg-destructive-foreground/10"
      >
        <RefreshCw className={cn("w-3 h-3 mr-1", isChecking && "animate-spin")} />
        {isChecking ? "Checking..." : "Retry"}
      </Button>
    </div>
  );
}
